// src/pages/TableroPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useObrasData } from '../hooks/useObrasData';
import ReactECharts from 'echarts-for-react';
import DashboardFilters from '../components/dashboard/DashboardFilters';
import { ComponenteMapas } from '../components/dashboard/ComponenteMapas';
import { GraficoSubsidiosAnual } from '../components/dashboard/GraficoSubsidiosAnual';
import { GraficoTortaMontos } from '../components/dashboard/GraficoTortaMontos';
import { GraficoTortaEstados } from '../components/dashboard/GraficoTortaEstados';

export const TableroPage = () => {
	const {
		metrics,
		proyectosPorPartido,
		proyectosPorEstado,
		proyectosPorPrograma, // 🎯 Consumimos los programas del hook
		proyectosPorAnio = [],
		proyectosPorPartidoBase, // 👈 Consumimos la foto base del año de forma directa
		proyectosPorSubsidioAnual,
		distribucionMontos,
		tiposPorArea,
		loading,
		isRefetching,
		error,
		filters,
		updateFilters,
		resetFilters
	} = useObrasData({ year: '2026', partido: 'Todas', tipo: 'Todos', subtipo: 'Todos', estado: 'Todos', area_responsable: 'Todos' });

	console.log("debug metrics", metrics);

	const [mapaBaseTodos, setMapaBaseTodos] = useState([]); //estado para el mapeo de todos los municipios, aunque se seleccione uno solo

	const [mostrarTop15, setMostrarTop15] = useState(true);

	// 📊 OPCIONES ECHARTS: Municipios Alcanzados (Gauge Limpio estilo PDF)
	const optionGauge = {
		series: [{
			type: 'gauge',
			max: 135, // Total de municipios de PBA
			startAngle: 210,
			endAngle: -30,
			progress: {
				show: true,
				width: 14,
				itemStyle: { color: '#3b82f6' } // Color azul de la barra
			},
			axisLine: {
				lineStyle: { width: 14, color: [[1, '#e5e7eb']] } // Fondo gris claro
			},
			pointer: { show: false }, // Ocultamos la aguja molesta
			axisTick: { show: false }, // Quitamos las rayitas intermedias
			splitLine: { show: false }, // Quitamos las líneas divisorias grandes
			axisLabel: {
				show: true,
				distance: -20,
				formatter: function (value) {
					if (value === 0 || value === 135) return value; // Solo mostramos extremos
					return '';
				},
				color: '#9ca3af',
				fontSize: 12
			},
			detail: {
				valueAnimation: true,
				formatter: '{value}',
				offsetCenter: [0, '10%'],
				textStyle: {
					fontSize: 42,
					fontWeight: 'bold',
					color: '#2563eb' // 🔵 Azul institucional (blue-600), igual que el value de Total Proyectos
				}
			},
			data: [{ value: metrics.municipiosAlcanzados || 0 }]
		}]
	};

	// 📊 OPCIONES ECHARTS: Proyectos por Partido (Barras horizontales del PDF)
	const optionBarrasHorizontal = {
		tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
		grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
		xAxis: { type: 'value' },
		yAxis: {
			type: 'category',
			data: proyectosPorPartido.map(p => p.partido).reverse(),
			axisLabel: { fontSize: 11 }
		},
		series: [{
			name: 'Proyectos',
			type: 'bar',
			data: proyectosPorPartido.map(p => parseInt(p.cantidad)).reverse(),
			itemStyle: { color: '#8cd45c' }, // El verde de tu PDF
			label: { show: true, position: 'right' }
		}]
	};
	const opcionesBarrasPartidos = useMemo(() => {
		// 🎯 MISMO PATRÓN QUE RED (Entidades por Partido): universo completo sin filtro de partido,
		// seleccionados primero (verde) y el resto opacado en gris
		const todos = proyectosPorPartidoBase.length > 0 ? proyectosPorPartidoBase : proyectosPorPartido;
		const filtroRaw = filters.partido;
		const sinFiltro =
			filtroRaw === 'Todas' || filtroRaw === 'Todos' || filtroRaw === undefined || filtroRaw === null ||
			(Array.isArray(filtroRaw) && (filtroRaw.length === 0 || filtroRaw.includes('Todos') || filtroRaw.includes('Todas')));
		const seleccionados = new Set(
			Array.isArray(filtroRaw) ? filtroRaw.filter(v => v !== 'Todos' && v !== 'Todas') : []
		);
		const limite = mostrarTop15 ? 15 : todos.length;
		const partidos = sinFiltro
			? todos.slice(0, limite)
			: [
				...todos.filter(p => seleccionados.has(String(p.partido_id))),
				...todos.filter(p => !seleccionados.has(String(p.partido_id))).slice(0, Math.max(0, limite - Math.min(seleccionados.size, todos.length))),
			];
		const esSeleccionado = (partidoId) =>
			sinFiltro || seleccionados.has(String(partidoId));
		const barras = partidos.map(p => ({
			value: parseInt(p.cantidad || 0),
			itemStyle: {
				color: esSeleccionado(p.partido_id) ? '#84cc16' : '#cbd5e1',
				borderRadius: [0, 4, 4, 0],
			},
		})).reverse();
		const coloresLabel = partidos.map(p => esSeleccionado(p.partido_id) ? '#374151' : '#9ca3af').reverse();

		return {
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'shadow' }
			},
			grid: {
				left: '3%',
				right: '8%', // Le damos un poquito más de aire a la derecha para que no se corten los números
				bottom: '3%',
				top: '3%',
				containLabel: true
			},
			xAxis: {
				type: 'value',
				// 🎯 CLAVAMOS LA ESCALA: Buscamos el valor más alto del array completo
				max: partidos.length > 0
					? Math.max(...partidos.map(p => parseInt(p.cantidad || 0)))
					: 'dataMax',
				splitLine: {
					lineStyle: { type: 'dashed', color: '#e5e7eb' }
				}
			},
			yAxis: {
				type: 'category',
				data: partidos.map(p => p.partido).reverse(),
				//inverse: true,
				axisLabel: {
					fontSize: 11,
					color: '#374151'
				},
				axisLine: { lineStyle: { color: '#d1d5db' } }
			},

			// 🎯 ELEGANCIA PURA: Scroll vertical nativo e interno
			/*
			dataZoom: !mostrarTop15 ? [
				{
					// 1. CONTROL DE RUEDA MOUSE (Invisible e Interno)
					type: 'inside',
					yAxisIndex: 0,
					// Usamos porcentajes para asegurar estabilidad en el scroll
					start: 0,
					end: 30, // Muestra aprox el 30% del volumen total de barras en pantalla
					zoomOnMouseWheel: false,       // ❌ Bloquea el zoom elástico tradicional
					moveOnMouseWheel: true,        // 🔥 Habilita que la rueda mueva la caja
					moveOnMouseMove: true,
					preventDefaultMouseMove: true, // 🛑 Evita que el navegador o el zoom nativo metan ruido
				},
				{
					// 2. CONTROL VISUAL (La barrita de scroll a la derecha)
					type: 'slider',
					yAxisIndex: 0,
					right: '1%',
					width: 10,
					borderColor: 'transparent',
					backgroundColor: '#f3f4f6',
					fillerColor: '#cbd5e1',
					handleSize: 0,
					showDetail: false,
					start: 0,
					end: 30
				}
			] : [], // Si está el Top 15 activo, queda vacío y sin scroll
			*/
			dataZoom: [
				{
					type: 'inside', // Scroll táctil / rueda del ratón
					yAxisIndex: 0,
					// 🚀 SI ES TODOS: Forzamos el foco en el extremo superior del gráfico (el final del array invertido)
					start: mostrarTop15 ? 0 : 85,  // Muestra el último 15% de los datos (la parte más alta)
					end: 100,
					zoomOnMouseWheel: false,       // ❌ Bloquea el zoom elástico tradicional
					moveOnMouseWheel: true,        // 🔥 Habilita que la rueda mueva la caja
					moveOnMouseMove: true,
					preventDefaultMouseMove: true, // 🛑 Evita que el navegador o el zoom nativo metan ruido
				},
				{
					type: 'slider', // La barra física que se ve a la derecha en tu captura
					yAxisIndex: 0,
					right: 10,
					width: 15,
					start: mostrarTop15 ? 0 : 85,  // Sincronizado: arranca el tirador arriba de todo
					end: 100,
					textStyle: { show: false } // Oculta números sueltos molestos en el slider
				}
			],

			series: [
				{
					name: 'Proyectos',
					type: 'bar',
					data: barras,
					label: {
						show: true,
						position: 'right',
						color: (params) => coloresLabel[params.dataIndex],
						fontWeight: 'bold',
						fontSize: 11
					}
				}
			]
		};
	}, [proyectosPorPartido, proyectosPorPartidoBase, filters.partido, mostrarTop15]);

	// 📊 OPCIONES ECHARTS: Torta de Programas (Estilo Anillo Moderno)
	const optionTortaProgramas = {
		tooltip: {
			trigger: 'item',
			formatter: '{b}: <b>{c}</b> ({d}%)'
		},
		legend: {
			type: 'scroll',      // 🔥 Permite navegación si los programas crecen
			orient: 'vertical',
			right: '5%',
			top: 'center',
			itemGap: 12,         // Espaciado balanceado entre ítems
			textStyle: {
				color: '#4b5563',
				fontSize: 12
			},
			// Formateador dinámico opcional para mejorar la lectura en la leyenda
			formatter: function (name) {
				// Buscamos el objeto correspondiente en tu array de datos
				const encontrado = proyectosPorPrograma.find(
					p => (p.programa || 'Sin Programa Asignado') === name
				);
				const cantidad = encontrado ? encontrado.cantidad : 0;

				// Si el nombre del programa es muy largo, lo recortamos para que no desborde
				const nombreCorto = name.length > 30 ? name.substring(0, 27) + '...' : name;

				// Retorna el formato: "Mejora del Hábitat (293)"
				return `${nombreCorto} (${cantidad})`;
			}
		},
		series: [
			{
				name: 'Programas',
				type: 'pie',
				radius: ['40%', '70%'], // Define el diseño de anillo (interno y externo)
				center: ['30%', '50%'], // Desplaza la torta a la derecha para dar espacio a la leyenda
				avoidLabelOverlap: true,

				// 🎯 PROPIEDAD CLAVE: Le da un grosor mínimo visual a las porciones ultra chicas (como la de 1 proyecto)
        		minAngle: 12,

				itemStyle: {
					borderRadius: 6,
					borderColor: '#fff',
					borderWidth: 2
				},
				label: {
					show: false, // Ocultamos las líneas con etiquetas externas para que no se encimen
					position: 'center'
				},
				emphasis: {
					label: {
						show: true,
						fontSize: 16,
						fontWeight: 'bold',
						formatter: '{b}'
					}
				},
				labelLine: {
					show: false
				},
				// Mapeamos dinámicamente la respuesta real de la base de datos
				// 🎯 EL MAPEO CLAVE: Aseguramos que ECharts lea 'name' y 'value'
				data: proyectosPorPrograma.map(p => ({
					// Matcheamos lo que venga del backend ('programa' y 'cantidad')
					name: p.programa || 'Sin Programa Asignado',
					value: parseInt(p.cantidad || 0)
				}))
			}
		]
	};

	// 📊 4. Barras Verticales (Proyectos por Año de Expediente Real)
	const optionBarrasAnio = {
		tooltip: { 
			trigger: 'axis',
			axisPointer: { type: 'shadow' },
			formatter: '{b}: <b>{c} proyectos</b>'
		},
		grid: { left: '4%', right: '4%', bottom: '3%', containLabel: true },
		xAxis: {
			type: 'category',
			data: proyectosPorAnio.map(a => a.anio),
			axisLabel: { fontSize: 11, color: '#4b5563' }
		},
		yAxis: { 
			type: 'value',
			splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }
		},
		series: [{
			name: 'Proyectos',
			type: 'bar',
			barWidth: '35%',
			// 🎯 MAPEADO CONDICIONAL DE COLOR POR BARRA
			data: proyectosPorAnio.map(a => {
				const cantidad = parseInt(a.cantidad || 0);
				const esAnioFiltrado = filters.year === a.anio;
				const ningunAnioFiltrado = filters.year === 'Todos';

				return {
					value: cantidad,
					itemStyle: {
						// Si es el año elegido o si están "Todos", va con el color pleno. Si no, se atenúa.
						color: (esAnioFiltrado || ningunAnioFiltrado) ? '#3b82f6' : '#cbd5e1', 
						borderRadius: [4, 4, 0, 0]
					}
				};
			}),
			label: { 
				show: true, 
				position: 'top',
				fontSize: 11,
				fontWeight: 'bold',
				color: '#374151'
			}
		}]
	};

	useEffect(() => {
		// Si el backend nos devolvió el listado completo de partidos (Modo "Todos")
		// y el estado testigo está vacío o cambió el año, congelamos la foto de la provincia.

		const esModoTodos = filters.partido.includes('Todos') || filters.partido.includes('Todos los Partidos');
		
		if (proyectosPorPartido && proyectosPorPartido.length > 0 && esModoTodos) {
			setMapaBaseTodos(proyectosPorPartido);
		}

		// Ojo: Si tu backend al cambiar de año devuelve el array completo, 
		// este efecto reacciona al instante actualizando la base del mapa para el nuevo año.
	}, [proyectosPorPartido]);

	if (loading) return <div className="p-8 text-center text-gray-600">Cargando tablero base...</div>;
	if (error) return <div className="p-8 text-center text-red-600">Error: {error}</div>;

	return (
		<div className="w-full p-6 bg-gray-50 min-h-screen">
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Tablero de Control</h1>
				<p className="text-sm text-gray-500">Métricas consolidadas</p>
				{/* Esta página quedó de respaldo cuando el tablero nuevo (/tableros) se
				    puso para todos los roles. El aviso va porque los dos tableros se
				    ven parecidos: sin esto no se sabe cuál de los dos se está
				    mirando. */}
				<div className="mt-3 inline-block px-3 py-1.5 text-xs font-medium rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
					Tablero anterior, de respaldo. El vigente está en Tableros.
				</div>
			</div>

			{/* Contenedor de Filtros */}
			<div className="relative mb-6">
				<DashboardFilters
					filters={filters}
					onFilterChange={(e) => updateFilters({ [e.target.name]: e.target.value })}
					onReset={resetFilters}
					loading={loading}
					isRefetching={isRefetching}
					tiposPorArea={tiposPorArea}
				/>
			</div>

			{/* Fila Superior de KPIs */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-3 xl:grid-cols-3">
				<div className="rounded-xl border bg-white p-6 shadow-sm text-center flex flex-col justify-center">
					<h3 className="text-sm font-medium text-gray-500 uppercase">Total Proyectos</h3>
					<p className="mt-2 text-4xl font-bold text-blue-600">{metrics.totalObras}</p>
				</div>

				<div className="rounded-xl border bg-white p-6 shadow-sm text-center flex flex-col justify-center">
					<h3 className="text-sm font-medium text-gray-500 uppercase">Monto Conveniado</h3>
					<p className="mt-2 text-3xl font-bold text-blue-600">
						${(metrics.presupuestoTotal / 1000000).toFixed(2)}M
					</p>
				</div>

				<div className="rounded-xl border bg-white p-6 shadow-sm flex flex-col items-center">
					<h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Municipios Alcanzados</h3>
					<div className="h-40 w-full">
						<ReactECharts option={optionGauge} style={{ height: '100%', width: '100%' }} />
					</div>
				</div>
			</div>

			{/* Fila del Medio: Partidos y Programas */}
			<div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
				{/* CARD IZQUIERDA: BARRAS PARTIDOS */}
				<div className="flex-1 bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[450px]"> {/* 🔥 Forzamos altura idéntica a la otra card */}
					<div className="flex justify-between items-center mb-4">
						<h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">
							{mostrarTop15 ? "Proyectos por Partido (Top 15)" : "Proyectos por Partido (Todos)"}
						</h3>
						<label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-md border border-gray-200 transition-colors select-none">
							<input
								type="checkbox"
								className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
								checked={mostrarTop15}
								onChange={(e) => setMostrarTop15(e.target.checked)}
							/>
							<span className="font-medium text-gray-700">Limitar a Top 15</span>
						</label>
					</div>

					{/* Contenedor con altura controlada para que el scroll de ECharts funcione impecable */}
					<div className="flex-1 w-full h-[340px]">
						<ReactECharts
							option={opcionesBarrasPartidos}
							style={{ height: '100%', width: '100%' }}
							notMerge={true}
						/>
					</div>
				</div>

				<div className="flex-1 bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[450px]">
					<h3 className="text-base font-bold text-gray-800 tracking-tight uppercase mb-4">Proyectos por Programa</h3>
					<div className="flex-1 w-full min-h-0">
						<ReactECharts option={optionTortaProgramas} style={{ height: '100%', width: '100%' }} />
					</div>
				</div>
			</div>

			<div className="mt-6">
				<ComponenteMapas
					// El fondo cromático de la provincia se alimenta de la foto base inmutable del año seleccionado
					proyectosPorPartido={proyectosPorPartidoBase}

					// Le pasamos el filtro de partidos actual para aplicar el contorno rojo de realce
					partidoSeleccionadoId={filters.partido}
				/>
			</div>

			{/* Fila Inferior: Gráfico de Barras Histórico por Año */}
			<div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
				<GraficoSubsidiosAnual 
					data={proyectosPorSubsidioAnual} 
					anioSeleccionado={filters.year}
				/>
				<GraficoTortaMontos 
					data={distribucionMontos}
				/>
			</div>

			{/* Fila Inferior: Gráfico de Barras Histórico por Año */}
			<div className="mt-6 grid grid-cols-1">
				<div className="rounded-xl border bg-white p-6 shadow-sm">
					<h3 className="text-base font-bold text-gray-800 tracking-tight uppercase mb-4">Proyectos por Año (Expedientes)</h3>
					<div className="h-72">
						<ReactECharts option={optionBarrasAnio} style={{ height: '100%', width: '100%' }} />
					</div>
				</div>
			</div>

			{/* Fila Inferior: Torta de Proyectos por Estado */}
			<div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
				<GraficoTortaEstados 
					data={proyectosPorEstado}
					estadosFiltro={filters.estado}
				/>
			</div>

		</div>
	);
};